import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Logo from "@/components/Logo";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Site-weite Passwort-Barriere. Blockiert das gesamte Frontend, bis das
 * Passwort korrekt eingegeben ist. Backend setzt bei Erfolg ein httpOnly-
 * Cookie `site_access`, das dann alle nachfolgenden API-Aufrufe autorisiert.
 * Ist `SITE_PASSWORD` serverseitig nicht gesetzt, meldet der Status-Endpoint
 * „disabled" → Gate ist transparent.
 */
export default function SiteGate({ children }) {
  const [state, setState] = useState("checking"); // checking | locked | unlocked
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/site-access/status`, { withCredentials: true });
        if (!mounted) return;
        if (r.data?.ok) setState("unlocked");
        else setState("locked");
      } catch {
        if (!mounted) return;
        setState("locked");
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (state === "locked") setTimeout(() => inputRef.current?.focus(), 50);
  }, [state]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw || busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/site-access`, { password: pw }, { withCredentials: true });
      setState("unlocked");
      setPw("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Passwort falsch.");
      setPw("");
      setTimeout(() => inputRef.current?.focus(), 50);
    } finally { setBusy(false); }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-muted-foreground text-sm">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
          Zugang wird geprüft …
        </div>
      </div>
    );
  }

  if (state === "locked") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="site-gate">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-8">
            <Logo variant="name" height={56} />
          </div>
          <div className="rounded-2xl border border-border bg-secondary/30 p-8">
            <div className="flex items-center gap-2 mb-1 text-primary">
              <Lock className="h-4 w-4" strokeWidth={1.5} />
              <div className="overline">Geschützter Bereich</div>
            </div>
            <h1 className="font-heading text-xl text-foreground mb-2">Zugriff nur mit Passwort</h1>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Diese Vorschau ist aktuell nur für einen kleinen Testkreis freigegeben. Bitte gib das
              Zugangspasswort ein.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
              <input
                ref={inputRef}
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                inputMode="text"
                value={pw}
                onChange={(e) => { setPw(e.target.value); if (error) setError(""); }}
                placeholder="Zugangspasswort"
                className="w-full h-11 rounded-full bg-background border border-border pl-4 pr-11 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 transition-colors"
                data-testid="site-gate-input"
                disabled={busy}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Passwort verbergen" : "Passwort anzeigen"}
                title={showPw ? "Passwort verbergen" : "Passwort anzeigen"}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground transition-colors"
                data-testid="site-gate-toggle-visibility"
              >
                {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
              </button>
              </div>
              {error && (
                <div className="text-xs text-[hsl(0_66%_72%)]" data-testid="site-gate-error">{error}</div>
              )}
              <button
                type="submit"
                disabled={busy || !pw}
                className="w-full h-11 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity inline-flex items-center justify-center gap-2"
                data-testid="site-gate-submit"
              >
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Prüfe …</>) : "Zugang öffnen"}
              </button>
            </form>
          </div>
          <p className="mt-6 text-center text-[11px] text-muted-foreground">
            TradeMind · Intelligence Platform
          </p>
        </div>
      </div>
    );
  }

  return children;
}
